
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, User, Home, Camera, Book } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleHomeClick = (e: React.MouseEvent) => {
    // If we're already on the home page, refresh and scroll to top
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Function to handle navigation with smooth scrolling
  const handleNavigation = (path: string, e?: React.MouseEvent) => {
    if (e && location.pathname === path) {
      e.preventDefault();
    }
    
    // Close mobile menu if open
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
    
    // If we're already on the target page, just scroll to top
    if (location.pathname === path) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Check if a path is active
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <header className={cn(
      "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
      isScrolled ? "bg-white/90 backdrop-blur-md shadow-md" : "bg-transparent"
    )}>
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center" onClick={handleHomeClick}>
          <div className="w-10 h-10 bg-pokemon-blue rounded-full flex items-center justify-center mr-2 transition-transform hover:scale-105">
            <div className="w-6 h-6 bg-white rounded-full border-4 border-pokemon-red"></div>
          </div>
          <span className="text-xl font-bold text-pokemon-blue">PokeScan</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-1">
          <Link 
            to="/" 
            className={cn(
              "px-3 py-2 rounded-md transition-colors flex items-center",
              isActive("/") 
                ? "bg-gray-100 text-pokemon-blue font-medium" 
                : "hover:bg-gray-100"
            )}
            onClick={(e) => handleNavigation("/", e)}
          >
            <Home className="w-4 h-4 mr-2" />
            <span>Home</span>
          </Link>
          <Link 
            to="/scan" 
            className={cn(
              "px-3 py-2 rounded-md transition-colors flex items-center",
              isActive("/scan") 
                ? "bg-gray-100 text-pokemon-blue font-medium" 
                : "hover:bg-gray-100"
            )}
            onClick={() => handleNavigation("/scan")}
          >
            <Camera className="w-4 h-4 mr-2" />
            <span>Scan Card</span>
          </Link>
          <Link 
            to="/collection" 
            className={cn(
              "px-3 py-2 rounded-md transition-colors flex items-center",
              isActive("/collection") 
                ? "bg-gray-100 text-pokemon-blue font-medium" 
                : "hover:bg-gray-100"
            )}
            onClick={() => handleNavigation("/collection")}
          >
            <Book className="w-4 h-4 mr-2" />
            <span>Collection</span>
          </Link>
        </nav>

        <div className="flex items-center space-x-3">
          <Link 
            to="/profile"
            onClick={() => handleNavigation("/profile")}
            className={cn(
              "px-3 py-2 rounded-md transition-colors flex items-center",
              isActive("/profile") 
                ? "bg-gray-100 text-pokemon-blue font-medium" 
                : "hover:bg-gray-100"
            )}
          >
            <User className="w-4 h-4 mr-2" />
            <span className="hidden md:inline">Account</span>
          </Link>
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </div>
      
      {/* Mobile Navigation Menu */}
      {isMenuOpen && isMobile && (
        <div className="md:hidden bg-white shadow-lg animate-fade-in">
          <div className="pt-2 pb-3 px-4 space-y-1">
            <Link 
              to="/" 
              className={cn(
                "block px-3 py-2 rounded-md transition-colors",
                isActive("/") 
                  ? "bg-gray-100 text-pokemon-blue font-medium" 
                  : "hover:bg-gray-100"
              )}
              onClick={(e) => {
                handleNavigation("/", e);
              }}
            >
              <div className="flex items-center">
                <Home className="w-4 h-4 mr-3" />
                <span>Home</span>
              </div>
            </Link>
            <Link 
              to="/scan" 
              className={cn(
                "block px-3 py-2 rounded-md transition-colors",
                isActive("/scan") 
                  ? "bg-gray-100 text-pokemon-blue font-medium" 
                  : "hover:bg-gray-100"
              )}
              onClick={() => handleNavigation("/scan")}
            >
              <div className="flex items-center">
                <Camera className="w-4 h-4 mr-3" />
                <span>Scan Card</span>
              </div>
            </Link>
            <Link 
              to="/collection" 
              className={cn(
                "block px-3 py-2 rounded-md transition-colors",
                isActive("/collection") 
                  ? "bg-gray-100 text-pokemon-blue font-medium" 
                  : "hover:bg-gray-100"
              )}
              onClick={() => handleNavigation("/collection")}
            >
              <div className="flex items-center">
                <Book className="w-4 h-4 mr-3" />
                <span>Collection</span>
              </div>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
